import { useEffect, useState } from 'react';
import { ChevronRight, Shuffle, Trash2, Plus, Minus } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { ConfirmationDrawer } from './ConfirmationDrawer';

export function MealCard({
  id,
  name,
  image,
  serves,
  onServesChange,
  onSwap,
  onRemove,
  onViewDetails
}) {
  const [localServes, setLocalServes] = useState(serves);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Sync local counter when viewing a different meal/day
  useEffect(() => {
    setLocalServes(serves);
  }, [serves, id]);

  const handleIncrement = (e) => {
    e.stopPropagation();
    const newServes = localServes + 1;
    setLocalServes(newServes);
    onServesChange(id, newServes);
  };

  const handleDecrement = (e) => {
    e.stopPropagation();
    if (localServes > 1) {
      const newServes = localServes - 1;
      setLocalServes(newServes);
      onServesChange(id, newServes);
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    setShowDeleteConfirm(true);
  };

  const confirmRemove = () => {
    onRemove(id);
    setShowDeleteConfirm(false);
  };

  const cancelRemove = () => {
    setShowDeleteConfirm(false);
  };

  return (
    <div
      className="bg-white rounded-3xl p-4 shadow-sm hover:shadow-md transition-shadow"
    >
      {/* Main Content */}
      <div className="flex items-center gap-4 mb-3">
        <div
          onClick={() => onViewDetails(id)}
          className="w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0 cursor-pointer"
        >
          <ImageWithFallback
            src={image}
            alt={name}
            className="w-full h-full object-cover"
          />
        </div>

        <div
          onClick={() => onViewDetails(id)}
          className="flex-1 min-w-0 cursor-pointer"
        >
          <h3 className="text-base mb-2">{name}</h3>
          
          {/* Serves Counter */}
          <div className="flex items-center gap-2">
            <span className="text-gray-500">Serves</span>
            <div className="flex items-center gap-1 bg-gray-50 rounded-full px-2 py-1">
              <button
                onClick={handleDecrement}
                className="w-6 h-6 flex items-center justify-center hover:bg-gray-200 rounded-full transition-colors"
                disabled={localServes <= 1}
              >
                <Minus className="w-3 h-3 text-gray-600" />
              </button>
              <span className="w-6 text-center">{localServes}</span>
              <button
                onClick={handleIncrement}
                className="w-6 h-6 flex items-center justify-center hover:bg-gray-200 rounded-full transition-colors"
              >
                <Plus className="w-3 h-3 text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        <button
          onClick={() => onViewDetails(id)}
          className="flex-shrink-0"
        >
          <ChevronRight className="w-5 h-5 text-[#C85534]" />
        </button>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSwap(id);
          }}
          className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-full bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <Shuffle className="w-4 h-4 text-gray-600" />
          <span className="text-gray-600">Swap</span>
        </button>

        <button
          onClick={handleRemove}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F3F4F6] hover:bg-[#B85A5A] transition-colors group"
        >
          <Trash2 className="w-4 h-4 text-[#525252] group-hover:text-white transition-colors" />
        </button>
      </div>

      {/* Confirmation Drawer */}
      <ConfirmationDrawer
        isOpen={showDeleteConfirm}
        onConfirm={confirmRemove}
        onCancel={cancelRemove}
        title="Remove Meal"
        message="Are you sure you want to remove this meal from your plan?"
        confirmLabel="Remove"
      />
    </div>
  );
}
